import { createContext, useContext } from "react";
import type { FunctionReturnType } from "convex/server";
import type { api } from "../../convex/_generated/api";

/**
 * Who is signed in, once, for the whole tree.
 *
 * **The contract: below `<AppChrome>`, the viewer is loaded and non-null.**
 * The shell already awaits `api.users.me` and refuses to render the `<Outlet>`
 * until it resolves — `undefined` shows the sign-in skeleton, `null` shows
 * `<NotProvisioned>` — so by the time a screen mounts, that question has been
 * answered. A screen must therefore neither re-query `users.me` nor re-guard
 * for `undefined`/`null`.
 *
 * It was ten screens each running their own `useQuery(api.users.me)` and each
 * writing its own `if (me === undefined) return <Loading/>` branch: ten extra
 * subscriptions to one document, and ten chances for a screen to disagree with
 * the shell about what an unresolved viewer looks like. The shell provides the
 * resolved document here, beside `OrgSettingsContext` and at exactly the same
 * point, and screens read it.
 *
 * The type is the query's own return type with the `null` arm removed, so a
 * field added to `users.me` arrives here with no edit, and a field removed
 * fails the build at the screen that reads it.
 */
export type Viewer = NonNullable<FunctionReturnType<typeof api.users.me>>;

/**
 * `null` is the "no provider" sentinel, never a signed-out viewer — the shell
 * only mounts the provider once it has a real document. `useViewer()` turns the
 * sentinel into a thrown error rather than letting a screen read `null.role`.
 */
export const ViewerContext = createContext<Viewer | null>(null);

export function useViewer(): Viewer {
  const viewer = useContext(ViewerContext);
  if (!viewer) {
    throw new Error(
      "useViewer() outside <ViewerContext.Provider>. Every screen renders " +
        "below the shell's AppChrome, which provides it; a component that " +
        "needs the viewer above the shell has to be handed one instead.",
    );
  }
  return viewer;
}
